import { useCurrentStateAndParams } from '@uirouter/react';
import { Code, History, AlertTriangle } from 'lucide-react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import LaptopCode from '@/assets/ico/laptop-code.svg?c';

import { PageHeader } from '@@/PageHeader';
import { findSelectedTabIndex, Tab, WidgetTabs } from '@@/Widget/WidgetTabs';
import { Icon } from '@@/Icon';
import { Badge } from '@@/Badge';

import { EventsDatatable } from '../../components/EventsDatatable';
import { useApplicationResource } from '../queries/useApplicationResource';

import { CustomResourceSummaryWidget } from './CustomResourceSummaryWidget';
import { CustomResourcePodsDatatable } from './CustomResourcePodsDatatable';
import { CustomResourceYAML } from './CustomResourceYAML';
import {
  useCustomResourceEventsTableData,
  useCustomResourceEventsTableState,
} from './useCustomResourceEventsTableData';

export function CustomResourceDetailsView({
  resourceType,
}: {
  resourceType: string;
}) {
  const environmentId = useEnvironmentId();

  const stateAndParams = useCurrentStateAndParams();
  const {
    params: { namespace, name },
  } = stateAndParams;

  const { data: application, isInitialLoading } = useApplicationResource(
    environmentId,
    namespace,
    name,
    resourceType
  );

  // events table data
  const { appEventsData, appEventWarningCount, isAppEventsTableLoading } =
    useCustomResourceEventsTableData(environmentId, namespace, application);
  const appEventsTableState = useCustomResourceEventsTableState();

  const tabs: Tab[] = [
    {
      name: 'Custom Resource',
      icon: LaptopCode,
      widget: (
        <CustomResourceSummaryWidget
          application={application}
          isInitialLoading={isInitialLoading}
        />
      ),
      selectedTabParam: 'application',
    },
    {
      name: (
        <div className="flex items-center gap-x-2">
          Events
          {appEventWarningCount >= 1 && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />
              {appEventWarningCount}
            </Badge>
          )}
        </div>
      ),
      icon: History,
      widget: (
        <EventsDatatable
          dataset={appEventsData}
          tableState={appEventsTableState}
          isLoading={isAppEventsTableLoading}
          data-cy="k8sCustomResourceDetail-eventsTable"
        />
      ),
      selectedTabParam: 'events',
    },
    {
      name: 'YAML',
      icon: Code,
      widget: (
        <CustomResourceYAML
          application={application}
          isInitialLoading={isInitialLoading}
        />
      ),
      selectedTabParam: 'YAML',
    },
  ];
  const currentTabIndex = findSelectedTabIndex(stateAndParams, tabs);

  return (
    <>
      <PageHeader
        title="Custom Resource details"
        breadcrumbs={[
          { label: 'Namespaces', link: 'kubernetes.resourcePools' },
          {
            label: namespace,
            link: 'kubernetes.resourcePools.resourcePool',
            linkParams: { id: namespace },
          },
          { label: 'Applications', link: 'kubernetes.applications' },
          name,
        ]}
        reload
      />
      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
      {tabs[currentTabIndex].widget}
      {application?.Pods && (
        <CustomResourcePodsDatatable pods={application?.Pods} />
      )}
    </>
  );
}
